import type {
  AppMeta,
  Asset,
  BacktestResult,
  BankrollSnapshot,
  BetIdea,
  Candle,
  ContractWatch,
  JournalEntry,
  Market,
  ModelPrediction,
  OddsQuote,
  Outcome,
  Position,
  RiskSettings,
  StrategyEvaluation,
  TradeIdea,
} from '../domain/types'
import { buildSampleMarkets, buildSampleOdds, STOCK_UNIVERSE } from './adapters'
import { addDays, generateCandles, todayIso } from './synthetic'
import { backtestStock, DEFAULT_COSTS, DEFAULT_STOCK_PARAMS } from '../lib/math/backtest'
import { mulberry32 } from '../lib/math/risk'
import { expectedValue } from '../lib/math/ev'

export interface SeedData {
  meta: AppMeta
  risk: RiskSettings
  assets: Asset[]
  markets: Market[]
  odds: OddsQuote[]
  tradeIdeas: TradeIdea[]
  betIdeas: BetIdea[]
  positions: Position[]
  journal: JournalEntry[]
  backtests: BacktestResult[]
  strategies: StrategyEvaluation[]
  bankrollHistory: BankrollSnapshot[]
  modelPredictions: ModelPrediction[]
  outcomes: Outcome[]
  watchlistAssetIds: string[]
  watchlistContractIds: string[]
  contractWatches: ContractWatch[]
}

const SECTORS: Record<string, string> = {
  NOVA: 'tech',
  ATLS: 'industrials',
  HELX: 'healthcare',
  ORCA: 'energy',
  VOLT: 'tech',
  CIRR: 'tech',
}

export const DEFAULT_RISK: RiskSettings = {
  bankroll: 10000,
  baseCurrency: 'USD',
  maxRiskPerTradePct: 0.01,
  maxDailyLossPct: 0.03,
  maxWeeklyLossPct: 0.07,
  maxExposurePerGroupPct: 0.2,
  maxCorrelatedPct: 0.25,
  maxSinglePositionPct: 0.1,
  kellyMode: 'quarter',
  kellyFraction: 0.25,
  minEdge: 0.02,
  cooldownLossesTrigger: 3,
  paperOnly: true,
}

export function createSeedAssets(days = 220): Asset[] {
  return STOCK_UNIVERSE.map((u) => ({
    id: `asset-${u.symbol.toLowerCase()}`,
    symbol: u.symbol,
    name: u.name,
    kind: 'stock',
    currency: 'USD',
    sector: SECTORS[u.symbol] ?? 'other',
    tags: [SECTORS[u.symbol] ?? 'other'],
    candles: decorateStockSample(u.symbol, generateCandles({ symbol: u.symbol, startPrice: u.startPrice, annualDrift: u.drift, annualVol: u.vol, days })),
  }))
}

function lastClose(asset: Asset): number {
  return asset.candles.length ? asset.candles[asset.candles.length - 1].close : 100
}

function buildTradeIdeas(assets: Asset[]): TradeIdea[] {
  const nova = assets.find((a) => a.symbol === 'NOVA')
  const orca = assets.find((a) => a.symbol === 'ORCA')
  const ideas: TradeIdea[] = []
  if (nova) {
    const price = lastClose(nova)
    ideas.push({
      id: 'trade-nova',
      assetId: nova.id,
      symbol: 'NOVA',
      direction: 'long',
      entry: price,
      stop: round2(price * 0.94),
      target: round2(price * 1.12),
      thesis: 'Uptrend intact, pullback to the 20-day EMA with rising volume. Risk defined below the swing low.',
      modelProb: 0.57,
      confidence: 0.62,
      dataQuality: 0.7,
      status: 'watch',
      createdAt: todayIso(),
      paper: true,
      tags: ['tech', 'momentum'],
      correlationGroup: 'tech',
    })
  }
  if (orca) {
    const price = lastClose(orca)
    ideas.push({
      id: 'trade-orca',
      assetId: orca.id,
      symbol: 'ORCA',
      direction: 'short',
      entry: price,
      stop: round2(price * 1.06),
      target: round2(price * 0.88),
      thesis: 'Downtrend, failed breakout, weak sector. Mean-reversion bounce into resistance offers a short.',
      modelProb: 0.54,
      confidence: 0.5,
      dataQuality: 0.55,
      status: 'idea',
      createdAt: todayIso(),
      paper: true,
      tags: ['energy'],
      correlationGroup: 'energy',
    })
  }
  const atls = assets.find((a) => a.symbol === 'ATLS')
  if (atls) {
    const price = lastClose(atls)
    ideas.push({
      id: 'trade-atls-breakout',
      assetId: atls.id,
      symbol: 'ATLS',
      direction: 'long',
      entry: round2(price),
      stop: round2(price * 0.96),
      target: round2(price * 1.1),
      thesis: 'Sample breakout plan: price is pressing resistance with stable volatility and defined ATR risk.',
      modelProb: 0.55,
      confidence: 0.58,
      dataQuality: 0.64,
      status: 'watch',
      createdAt: todayIso(),
      paper: true,
      tags: ['breakout', 'sample'],
      correlationGroup: 'industrials',
    })
  }
  const volt = assets.find((a) => a.symbol === 'VOLT')
  if (volt) {
    const price = lastClose(volt)
    ideas.push({
      id: 'trade-volt-squeeze',
      assetId: volt.id,
      symbol: 'VOLT',
      direction: 'long',
      entry: round2(price),
      stop: round2(price * 0.92),
      target: round2(price * 1.18),
      thesis: 'Sample volatility-squeeze plan: only paper trade if expansion confirms with volume.',
      modelProb: 0.53,
      confidence: 0.5,
      dataQuality: 0.52,
      status: 'idea',
      createdAt: todayIso(),
      paper: true,
      tags: ['squeeze', 'sample'],
      correlationGroup: 'tech',
    })
  }
  return ideas
}

function buildBetIdeas(odds: OddsQuote[]): BetIdea[] {
  const out: BetIdea[] = []
  const boston = odds.find((o) => o.id === 'odds-bos-ml')
  if (boston) {
    out.push({
      id: 'bet-bos',
      kind: 'sportsbook',
      refId: boston.id,
      title: 'Boston ML vs Denver',
      sport: 'NBA',
      modelProb: boston.modelProb ?? 0.57,
      decimalOdds: boston.decimalOdds,
      stake: 150,
      feePct: 0,
      status: 'watch',
      createdAt: todayIso(),
      paper: true,
      tags: ['nba'],
      correlationGroup: 'nba-bos-den',
      closingDecimal: boston.closingDecimal,
      result: 'pending',
    })
  }
  out.push({
    id: 'bet-fed-hold',
    kind: 'prediction',
    refId: 'ct-fed-hold',
    title: 'Fed HOLD contract @ 59¢',
    modelProb: 0.54,
    decimalOdds: 100 / 59,
    stake: 0,
    feePct: 0.01,
    status: 'skipped',
    createdAt: todayIso(),
    paper: true,
    tags: ['macro'],
    correlationGroup: 'us-rates',
    result: 'pending',
  })
  return out
}

function buildPositions(assets: Asset[]): Position[] {
  const nova = assets.find((a) => a.symbol === 'NOVA')
  const price = nova ? lastClose(nova) : 140
  return [
    {
      id: 'pos-nova',
      kind: 'stock',
      label: 'NOVA (paper)',
      direction: 'long',
      size: 15,
      entryPrice: round2(price * 0.97),
      currentPrice: price,
      stake: round2(price * 0.97 * 15),
      openedAt: addDays(todayIso(), -6),
      status: 'open',
      paper: true,
      correlationGroup: 'tech',
    },
    {
      id: 'pos-cpi',
      kind: 'prediction',
      label: 'CPI Below 3.0% @ 46¢ (paper)',
      direction: 'long',
      size: 100,
      entryPrice: 0.46,
      currentPrice: 0.49,
      stake: 46,
      openedAt: addDays(todayIso(), -2),
      status: 'open',
      paper: true,
      correlationGroup: 'us-rates',
    },
  ]
}

function buildJournal(): JournalEntry[] {
  return [
    {
      id: 'jr-1',
      createdAt: addDays(todayIso(), -5),
      kind: 'stock',
      title: 'NOVA pullback long',
      thesis: 'Buy the dip to EMA in an uptrend.',
      probabilityEstimate: 0.57,
      marketPriceOrOdds: 'entry 138.2',
      expectedValue: 0.08,
      stake: 200,
      reasonEnter: 'Trend + defined risk below swing low.',
      reasonSkip: '',
      emotionalState: 'calm',
      result: 'win',
      realizedPnl: 120,
      lessons: 'Sizing felt right; let winner run to target.',
      followedRules: true,
      tags: ['tech'],
    },
    {
      id: 'jr-2',
      createdAt: addDays(todayIso(), -3),
      kind: 'sportsbook',
      title: 'Chased a late NBA total',
      thesis: 'Thought the line was slow.',
      probabilityEstimate: 0.55,
      marketPriceOrOdds: '-110',
      expectedValue: -0.02,
      stake: 120,
      reasonEnter: 'Wanted to get even after a loss.',
      reasonSkip: '',
      emotionalState: 'revenge',
      result: 'loss',
      realizedPnl: -120,
      lessons: 'Negative EV and tilt. This is exactly the trade to skip.',
      followedRules: false,
      tags: ['nba'],
    },
    {
      id: 'jr-3',
      createdAt: addDays(todayIso(), -1),
      kind: 'prediction',
      title: 'Fed HOLD — skipped',
      thesis: 'Edge too thin after fees.',
      probabilityEstimate: 0.54,
      marketPriceOrOdds: '59¢',
      expectedValue: -0.01,
      stake: 0,
      reasonEnter: '',
      reasonSkip: 'Break-even is 59.6%; my 54% has no edge after the 1% fee.',
      emotionalState: 'calm',
      result: 'pending',
      lessons: 'Good discipline — logged the no-bet.',
      followedRules: true,
      tags: ['macro'],
    },
  ]
}

function buildBankrollHistory(start: number): BankrollSnapshot[] {
  const rng = mulberry32(424242)
  const out: BankrollSnapshot[] = []
  let balance = start * 0.92
  for (let i = 30; i >= 0; i--) {
    balance *= 1 + (rng() - 0.45) * 0.012
    out.push({
      id: `bk-${i}`,
      date: addDays(todayIso(), -i),
      balance: round2(balance),
      openRisk: round2(balance * 0.02 * rng()),
      realizedPnl: round2((rng() - 0.5) * 80),
    })
  }
  return out
}

function buildStockBacktests(assets: Asset[]): BacktestResult[] {
  const nova = assets.find((a) => a.symbol === 'NOVA')
  const atls = assets.find((a) => a.symbol === 'ATLS')
  const out: BacktestResult[] = []
  if (nova) {
    const result = backtestStock(nova.candles, { ...DEFAULT_STOCK_PARAMS, type: 'sma-cross', fast: 20, slow: 50 }, DEFAULT_COSTS)
    out.push({
      id: 'bt-sample-nova-sma',
      name: 'NOVA sample SMA crossover',
      createdAt: todayIso(),
      strategy: 'sma-cross',
      params: { fast: 20, slow: 50 },
      metrics: result.metrics,
      inSample: result.inSample,
      outOfSample: result.outOfSample,
      equityCurve: result.equityCurve,
      overfitWarning: result.overfitWarning,
    })
  }
  if (atls) {
    const result = backtestStock(atls.candles, { ...DEFAULT_STOCK_PARAMS, type: 'breakout', breakoutLookback: 20 }, DEFAULT_COSTS)
    out.push({
      id: 'bt-sample-atls-breakout',
      name: 'ATLS sample breakout',
      createdAt: todayIso(),
      strategy: 'breakout',
      params: { breakoutLookback: 20 },
      metrics: result.metrics,
      inSample: result.inSample,
      outOfSample: result.outOfSample,
      equityCurve: result.equityCurve,
      overfitWarning: result.overfitWarning,
    })
  }
  return out
}

function buildPredictionsAndOutcomes(): { predictions: ModelPrediction[]; outcomes: Outcome[] } {
  const rng = mulberry32(13371337)
  const probs = [0.2, 0.3, 0.35, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.8]
  const predictions: ModelPrediction[] = []
  const outcomes: Outcome[] = []
  for (let i = 0; i < 30; i++) {
    const prob = probs[i % probs.length]
    const outcome: 0 | 1 = rng() < prob ? 1 : 0
    const decimalOdds = 1 / Math.max(0.02, prob - 0.03)
    const ev = expectedValue({ modelProb: prob, decimalOdds })
    predictions.push({
      id: `mp-${i}`,
      createdAt: addDays(todayIso(), -(30 - i)),
      label: `Resolved idea #${i + 1}`,
      category: i % 3 === 0 ? 'stock' : i % 3 === 1 ? 'sportsbook' : 'prediction',
      inputs: {
        baseProb: prob,
        recentForm: 0,
        historicalWinRate: prob,
        volatility: 0.3,
        marketMovement: 0,
        notes: '',
        confidence: 0.6,
        dataQuality: 0.65,
      },
      estProb: prob,
      ciLow: Math.max(0, prob - 0.1),
      ciHigh: Math.min(1, prob + 0.1),
      marketProb: Math.max(0.01, prob - 0.03),
      edge: 0.03,
      ev: ev.evPerUnit,
      action: ev.decision,
      resolved: true,
      outcome,
    })
    outcomes.push({
      id: `oc-${i}`,
      refId: `mp-${i}`,
      refType: 'model',
      settledAt: addDays(todayIso(), -(30 - i) + 1),
      result: outcome ? 'win' : 'loss',
      pnl: outcome ? round2((decimalOdds - 1) * 50) : -50,
      predictedProb: prob,
      closingDecimal: decimalOdds * 0.98,
    })
  }
  return { predictions, outcomes }
}

export function createSeedData(): SeedData {
  const assets = createSeedAssets()
  const markets: Market[] = buildSampleMarkets()
  const odds: OddsQuote[] = buildSampleOdds()
  const { predictions, outcomes } = buildPredictionsAndOutcomes()

  return {
    meta: {
      acceptedLegalDisclaimer: false,
      jurisdiction: 'unset',
      paperFirstMode: true,
      createdAt: new Date().toISOString(),
      version: '0.1.0',
    },
    risk: { ...DEFAULT_RISK },
    assets,
    markets,
    odds,
    tradeIdeas: buildTradeIdeas(assets),
    betIdeas: buildBetIdeas(odds),
    positions: buildPositions(assets),
    journal: buildJournal(),
    backtests: buildStockBacktests(assets),
    strategies: [],
    bankrollHistory: buildBankrollHistory(DEFAULT_RISK.bankroll),
    modelPredictions: predictions,
    outcomes,
    watchlistAssetIds: ['asset-nova', 'asset-volt'],
    watchlistContractIds: ['ct-launch-no', 'ct-cpi-under'],
    contractWatches: [
      {
        contractId: 'ct-launch-no',
        side: 'yes',
        targetPriceCents: 70,
        note: 'Only worth tracking if delay pricing cools off.',
        addedAt: todayIso(),
        baselineYesCents: 73,
      },
      {
        contractId: 'ct-cpi-under',
        side: 'yes',
        targetPriceCents: 44,
        note: 'Enter research queue if ask falls below the model line.',
        addedAt: todayIso(),
        baselineYesCents: 46,
      },
    ],
  }
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}

function decorateStockSample(symbol: string, candles: Candle[]): Candle[] {
  const out = candles.map((c) => ({ ...c }))
  if (out.length < 80) return out

  if (symbol === 'ATLS') {
    const start = out.length - 30
    for (let i = start; i < out.length; i++) {
      const lift = 1 + (i - start) * 0.0025
      out[i] = normalizeCandle({ ...out[i], close: round2(out[i].close * lift), high: round2(out[i].high * lift), low: round2(out[i].low * lift), volume: Math.round(out[i].volume * 1.25) })
    }
  }

  if (symbol === 'VOLT') {
    const start = out.length - 45
    for (let i = start; i < out.length - 8; i++) {
      const midpoint = (out[i].open + out[i].close) / 2
      out[i] = normalizeCandle({
        ...out[i],
        open: round2(midpoint * 0.998),
        close: round2(midpoint * 1.002),
        high: round2(midpoint * 1.01),
        low: round2(midpoint * 0.99),
        volume: Math.round(out[i].volume * 0.8),
      })
    }
    for (let i = out.length - 8; i < out.length; i++) {
      out[i] = normalizeCandle({ ...out[i], close: round2(out[i].close * 1.015), high: round2(out[i].high * 1.02), volume: Math.round(out[i].volume * 1.8) })
    }
  }

  if (symbol === 'HELX') {
    const idx = out.length - 18
    const prev = out[idx - 1]
    const open = round2(prev.close * 0.94)
    out[idx] = normalizeCandle({ ...out[idx], open, high: round2(open * 1.04), low: round2(open * 0.98), close: round2(prev.close * 0.97), volume: Math.round(out[idx].volume * 2.2) })
  }

  return out
}

function normalizeCandle(c: Candle): Candle {
  const high = Math.max(c.high, c.open, c.close)
  const low = Math.min(c.low, c.open, c.close)
  return { ...c, high: round2(high), low: round2(Math.max(0.01, low)), open: round2(c.open), close: round2(c.close) }
}
